import { Fragment } from "react"
import { Listbox, Transition } from "@headlessui/react"
import { Check } from "lucide-react"
import Tooltip from "../tool-tip"
import {
  classNames,
  findSelectOptionName,
  getSelectedOptionHTML
} from "../../utils"

/**
 * This is a wrapper for Headless UI's Listbox component
 * https://headlessui.com/react/listbox
 * Usually should be kept as a low-level component and not used directly,
 * instead use a use custom wrapper component(Ex. src/common/ui/integration-owner-selector/index.tsx)
 */

type Props = {
  data: readonly any[]
  selected: any
  label: string
  name?: string
  IDkey?: string
  isLoading?: boolean
  disabled?: boolean
  multiple?: boolean
  showLabel?: boolean
  error?: string
  isError?: boolean
  required?: boolean
  size?: "sm" | "md"
  selectClassName?: string
  buttonClassName?: string
  tooltipEnabled?: boolean
  setSelected: (item: any) => void
}

const ID_KEY_DEFAULT = "id"

const Select = ({
  data,
  selected,
  IDkey = ID_KEY_DEFAULT,
  setSelected,
  isLoading,
  multiple,
  disabled,
  name,
  label,
  showLabel = true,
  error,
  isError = false,
  required = false,
  size = "md",
  selectClassName = "",
  buttonClassName = "",
  tooltipEnabled = false
}: Props) => {
  const selectedLabel = findSelectOptionName(selected, data)
  const hasError = isError || !!error

  return (
    <div className="relative">
      <Listbox
        name={name}
        value={
          multiple
            ? selected
            : typeof selected === "string"
            ? selected
            : selected?.[IDkey]
        }
        onChange={setSelected}
        multiple={multiple}
        disabled={disabled || isLoading}
      >
        {({ open }) => (
          <>
            <Listbox.Label
              className={`block font-medium ${showLabel ? "" : "sr-only"}`}
            >
              {label} {required && <span className="text-red-400">*</span>}
            </Listbox.Label>
            <div
              className={`relative ${selectClassName} ${
                label && showLabel ? "mt-1" : ""
              }`}
            >
              {tooltipEnabled && !!selected?.tooltipText ? (
                <Tooltip
                  toolTipNode={getSelectedOptionHTML(
                    selected,
                    buttonClassName,
                    multiple,
                    isLoading,
                    selectedLabel,
                    size,
                    hasError
                  )}
                  text={selected.tooltipText}
                />
              ) : (
                getSelectedOptionHTML(
                  selected,
                  buttonClassName,
                  multiple,
                  isLoading,
                  selectedLabel,
                  size,
                  hasError
                )
              )}

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {data.map(item => (
                    <Listbox.Option
                      key={item[IDkey]}
                      className={({ active }) =>
                        classNames(
                          active
                            ? "text-white bg-primary-500"
                            : "text-gray-900",
                          size === "sm"
                            ? "relative select-none py-1.5 pl-6 pr-3 text-xs cursor-pointer"
                            : "relative select-none py-2 pl-7 pr-4 cursor-pointer"
                        )
                      }
                      value={item[IDkey]}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={classNames(
                              selected ? "font-medium" : "font-normal",
                              "block truncate"
                            )}
                          >
                            {item?.icon ? (
                              <span className="flex items-center">
                                <span className="pr-2">{item.icon}</span>
                                <span className="text-sm md:text-base">
                                  {item.name}
                                </span>
                              </span>
                            ) : (
                              item.name
                            )}
                          </span>

                          {selected ? (
                            <span
                              className={classNames(
                                active ? "text-white" : "text-primary-500",
                                "absolute inset-y-0 left-0 flex items-center pl-2"
                              )}
                            >
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </div>
  )
}

export default Select
